import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PartnerPortal from '../../components/PartnerPortal';
import ApplyPartnerForm from '../../components/PartnerPortal/ApplyPartnerForm';
import ROIofPurity from '../../components/PartnerPortal/ROIofPurity';
import AtmosicIoT from '../../components/PartnerPortal/AtmosicIoT';
import ThreeKProtocol from '../../components/Philosophy/ThreeKProtocol';
import OracleHero, { isPartnerUnlocked } from '../../components/PartnerPortal/OracleHero';
import { fetchPartners } from '../../lib/api';

/**
 * /partners — Oracle-mode entry.
 *
 * Hero is the single B2B email gate. Until a hotelier names themselves,
 * the rest of the page is sealed. Once unlocked, three thresholds
 * reveal in order: I. Digital Product Passport (the partner dashboard),
 * II. Atmosic IoT sovereign infrastructure, III. ROI of Purity audit.
 * The apply form remains as a quiet closing.
 */

const PartnerPortalPage = () => {
  const [params, setParams] = useSearchParams();
  const [partners, setPartners] = useState([]);
  const [activeId, setActiveId] = useState(params.get('partner') || '');
  const [unlocked, setUnlocked] = useState(false);

  // Re-honour a prior gate-opening (refresh / return visit)
  useEffect(() => {
    setUnlocked(isPartnerUnlocked());
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    fetchPartners()
      .then((list) => {
        setPartners(list);
        if (!activeId && list.length) {
          const first = list[0].id;
          setActiveId(first);
          setParams({ partner: first });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  const activePartner = partners.find((p) => p.id === activeId);

  const onSelect = (id) => {
    setActiveId(id);
    setParams({ partner: id });
  };

  return (
    <div data-testid="partner-portal-page">
      <OracleHero
        onUnlocked={() => setUnlocked(true)}
        alreadyUnlocked={unlocked}
      />

      {unlocked && (
        <>
          {/* === THRESHOLD I — Digital Product Passport === */}
          <section
            id="digital-product-passport"
            data-testid="partner-step-i"
            className="px-6 md:px-12 pt-24 md:pt-32 pb-12 bg-[#F4F8F9]"
          >
            <div className="max-w-[1400px] mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-12">
                <div className="md:col-span-7">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-6">
                    <span className="inline-block w-7 h-px bg-[#90B0BF] mr-3 align-middle" />
                    Threshold I · Digital Product Passport
                  </p>
                  <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#7C9CB1] font-light leading-[1.05]">
                    A private house
                    <br />
                    for our <em className="text-[#90B0BF]">houses</em>.
                  </h2>
                </div>
                <div className="md:col-span-5 md:pt-4">
                  <p className="font-serif italic text-lg text-[#7C9CB1]/90 leading-relaxed max-w-md">
                    Every property holds a Digital Product Passport — a
                    living ledger of verified refills, pay-per-use dispenses,
                    atelier-direct allocations, and a soulbound certificate
                    that names you in the genesis circle.
                  </p>
                </div>
              </div>

              {partners.length > 0 && (
                <div
                  data-testid="partner-switcher"
                  className="flex flex-wrap gap-3 mb-10"
                >
                  {partners.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => onSelect(p.id)}
                      data-testid={`partner-switch-${p.id}`}
                      className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-medium border transition-colors ${
                        p.id === activeId
                          ? 'border-[#7C9CB1] bg-[#7C9CB1] text-[#F4F8F9]'
                          : 'border-[#C1D7E2] text-[#7C9CB1] hover:bg-[#C1D7E2]/40'
                      }`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              )}

              {activePartner ? (
                <PartnerPortal partnerId={activeId} />
              ) : (
                <div
                  data-testid="partner-no-selection"
                  className="border border-[#C1D7E2] bg-[#F4F8F9] p-16 text-center"
                >
                  <p className="font-serif italic text-2xl text-[#7C9CB1]">
                    Loading the houses…
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* === THRESHOLD II — Atmosic IoT === */}
          <AtmosicIoT />

          {/* The 3K Protocol — biological+digital cycle in one slide */}
          <ThreeKProtocol
            testId="partner-three-k-protocol"
            overline="The 3K Protocol · the complete cycle, signed"
          />

          {/* === THRESHOLD III — ROI of Purity Audit === */}
          <div data-testid="partner-step-iii" id="roi-of-purity">
            <ROIofPurity />
          </div>

          {/* Closing — Apply form */}
          <section
            id="apply"
            data-testid="partner-apply"
            className="px-6 md:px-12 py-24 md:py-32 bg-[#0E1A22] text-[#F4F8F9]"
          >
            <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/75 mb-4">
                  <span className="inline-block w-7 h-px bg-[#F4F8F9]/75 mr-3 align-middle" />
                  Apply to become an Atelier Node
                </p>
                <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05]">
                  Host the ritual.
                  <br />
                  Own the ledger.
                </h2>
                <p className="mt-6 text-[#F4F8F9]/85 font-light leading-relaxed max-w-md">
                  Independent properties — hotels, spas and retreats that
                  own their philosophy — receive a soulbound partner
                  certificate, a private API key for the refill + usage
                  pipelines, and a $SEVA balance that signs against our
                  atelier-direct allocation.
                </p>
                <p
                  data-testid="partner-apply-scheduling-note"
                  className="mt-6 font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4F8F9]/65 leading-relaxed"
                >
                  Scheduling link provided upon handshake.
                </p>
                <p className="mt-2 font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4F8F9]/65">
                  The Atelier · B2B Operations · Heavener, Oklahoma
                </p>
              </div>
              <ApplyPartnerForm />
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default PartnerPortalPage;
